import {
  ContentPage,
  ContentSection,
  ContactEmail,
  PostalAddress,
} from '../components/ContentPage.js';
import type { FooterView } from '../components/SiteFooter.js';
import { useT } from '../i18n/index.js';

/**
 * What happens to a visitor's data.
 *
 * Two separate rules ask for this page and neither is optional: German and EU
 * law (Art. 13 GDPR) wants it because the site processes personal data at all.
 * The AdSense programme policies used to be the second reason; the site no
 * longer carries ads, and that section is commented out below.
 *
 * It describes what the code actually does. The two cookies named are the two
 * `res.cookie` calls in the server, the local-storage entries are the four
 * `brandedKey` callers, and "nothing is requested from YouTube before the
 * first play" is what `TrailerPlayer` enforces. Anything that stops being
 * true in the code has to stop being written here on the same commit.
 */

/**
 * Bumped by hand when the text changes, because that is the only honest way:
 * a build date would move on every deploy and claim a review that never
 * happened.
 */
const LAST_UPDATED = '2026-10-16';

export function PrivacyScreen({
  onBack,
  onNavigate,
}: {
  onBack: () => void;
  onNavigate: (view: FooterView) => void;
}) {
  const t = useT();

  return (
    <ContentPage
      view="privacy"
      title={t('privacy.title')}
      lead={t('privacy.lead')}
      onBack={onBack}
      onNavigate={onNavigate}
    >
      <p className="muted small">
        {t('privacy.updated', { date: new Date(LAST_UPDATED).toLocaleDateString() })}
      </p>

      <ContentSection title={t('privacy.controllerTitle')}>
        <p>{t('privacy.controllerBody')}</p>
        <PostalAddress />
        <p>
          <ContactEmail />
        </p>
      </ContentSection>

      <ContentSection title={t('privacy.hostingTitle')}>
        <p>{t('privacy.hostingBody1')}</p>
        <p>{t('privacy.hostingBody2')}</p>
      </ContentSection>

      <ContentSection title={t('privacy.usageTitle')}>
        <p>{t('privacy.usageBody1')}</p>
        <p>{t('privacy.usageBody2')}</p>
        <p>{t('privacy.usageBody3')}</p>
      </ContentSection>

      <ContentSection title={t('privacy.storageTitle')}>
        <p>{t('privacy.storageBody')}</p>
        <ul className="content__list">
          <li>{t('privacy.storageSession')}</li>
          <li>{t('privacy.storageLogin')}</li>
          <li>{t('privacy.storageLocale')}</li>
          <li>{t('privacy.storageRound')}</li>
        </ul>
        <p>{t('privacy.storageBody2')}</p>
      </ContentSection>

      <ContentSection title={t('privacy.accountTitle')}>
        <p>{t('privacy.accountBody1')}</p>
        <p>{t('privacy.accountBody2')}</p>
      </ContentSection>

      {/* The site shows no advertising any more, so there is nothing for this
          section to disclose. Kept, commented out, alongside the rest of the
          switched-off ad code.
      <ContentSection title={t('privacy.adsTitle')}>
        <p>{t('privacy.adsBody1')}</p>
        <p>{t('privacy.adsBody2')}</p>
        <ul className="content__list">
          <li>
            <a href="https://adssettings.google.com" target="_blank" rel="noreferrer noopener">
              {t('privacy.adsSettingsLink')}
            </a>
          </li>
          <li>
            <a
              href="https://policies.google.com/technologies/partner-sites"
              target="_blank"
              rel="noreferrer noopener"
            >
              {t('privacy.adsPolicyLink')}
            </a>
          </li>
        </ul>
        <p>{t('privacy.adsBody3')}</p>
      </ContentSection>
      */}

      <ContentSection title={t('privacy.tmdbTitle')}>
        <p>{t('privacy.tmdbBody')}</p>
      </ContentSection>

      <ContentSection title={t('privacy.trailersTitle')}>
        <p>{t('privacy.trailersBody')}</p>
      </ContentSection>

      <ContentSection title={t('privacy.rightsTitle')}>
        <p>{t('privacy.rightsBody')}</p>
        <ul className="content__list">
          <li>{t('privacy.rightsAccess')}</li>
          <li>{t('privacy.rightsRectify')}</li>
          <li>{t('privacy.rightsErase')}</li>
          <li>{t('privacy.rightsRestrict')}</li>
          <li>{t('privacy.rightsPortability')}</li>
          <li>{t('privacy.rightsComplain')}</li>
        </ul>
      </ContentSection>

      <ContentSection title={t('privacy.retentionTitle')}>
        <p>{t('privacy.retentionBody')}</p>
      </ContentSection>

      <ContentSection title={t('privacy.changesTitle')}>
        <p>{t('privacy.changesBody')}</p>
      </ContentSection>
    </ContentPage>
  );
}
